export type Role = 'USER' | 'ADMIN';
export type RoleCheck = Role | Role[];

export interface ApiMessageResponse {
  message?: string;
}

export type CalendarProvider = 'GOOGLE' | 'OUTLOOK' | 'ICS';
export type BookingStatus = 'REQUESTED' | 'CONFIRMED' | 'CANCELLED';

export interface AuthUser {
  token?: string | null;
  userId: string;
  email: string;
  name: string;
  phoneNumber?: string;
  role: Role;
  crispTokenId: string;
}

export interface UserRecord {
  id: string;
  email: string;
  name: string;
  role: Role;
  provider?: string | null;
  createdAt?: string | null;
}


export interface ContactMessage {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  createdAt?: string | null;
}

export interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}

export interface ConsultationService {
  id: string;
  title: string;
  summary: string;
  image: string;
  durationMinutes: number;
}

export interface ServiceItem {
  id: string;
  titleEn: string;
  titleAr: string;
  descEn?: string;
  descAr?: string;
  imageUrl?: string;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ServiceRequestData {
  titleEn: string;
  titleAr: string;
  descEn?: string;
  descAr?: string;
  imageUrl?: string;
  sortOrder: number;
}

export interface ProductItem {
  id: string;
  nameEn: string;
  nameAr: string;
  descEn?: string;
  descAr?: string;
  sizeEn?: string;
  sizeAr?: string;
  imageUrls: string[];
  serviceId: string;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProductRequestData {
  nameEn: string;
  nameAr: string;
  descEn?: string;
  descAr?: string;
  sizeEn?: string;
  sizeAr?: string;
  imageUrls: string[];
  serviceId: string;
  sortOrder: number;
}

export interface QuotationPayload {
  name: string;
  companyName?: string;
  phone: string;
  city: string;
  email: string;
  description?: string;
  productId: string;
}

export interface QuotationItem {
  id: string;
  name: string;
  companyName?: string;
  phone: string;
  city: string;
  email: string;
  description?: string;
  productId: string;
  productNameEn?: string;
  productNameAr?: string;
  createdAt?: string;
}

export interface ConsultationServiceItem {
  id: string;
  titleEn: string;
  titleAr: string;
  summaryEn?: string;
  summaryAr?: string;
  imageUrl?: string;
  durationMinutes?: number;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ConsultationServiceRequestData {
  titleEn: string;
  titleAr: string;
  summaryEn?: string;
  summaryAr?: string;
  imageUrl?: string;
  durationMinutes?: number;
  sortOrder: number;
}

export interface TeamMemberRecord {
  id: string;
  nameEn: string;
  nameAr: string;
  titleEn: string;
  titleAr: string;
  imageUrl?: string;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface TeamMemberRequestData {
  nameEn: string;
  nameAr: string;
  titleEn: string;
  titleAr: string;
  imageUrl?: string;
  sortOrder: number;
}

export interface BookingRequestData {
  serviceId: string;
  serviceName: string;
  customerName: string;
  email: string;
  phone: string;
  company?: string;
  startAt: string;
  endAt: string;
  timezone: string;
  calendarProvider: string;
  locale: string;
  notes?: string;
}

export interface BookingUpdateRequestData {
  serviceId?: string;
  serviceName?: string;
  customerName?: string;
  email?: string;
  phone?: string;
  company?: string;
  startAt?: string;
  endAt?: string;
  timezone?: string;
  notes?: string;
  status?: BookingStatus;
}

export interface BookingCalendarOption {
  provider: CalendarProvider;
  url: string;
  primary: boolean;
  download: boolean;
}

export interface BookingRecord {
  id: string;
  userId?: string;
  serviceId: string;
  serviceName: string;
  customerName: string;
  email: string;
  phone: string;
  company?: string | null;
  startAt: string;
  endAt: string;
  timezone: string;
  calendarProvider: CalendarProvider;
  status: BookingStatus;
  locale: 'en' | 'ar';
  notes?: string | null;
  meetingLink?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface BookingCreateResponse extends BookingRecord {
  calendarOptions: BookingCalendarOption[];
}

export interface AvailabilitySlot {
  startAt: string;
  endAt: string;
  label: string;
  booked: boolean;
}

export interface AvailabilityResponse {
  date: string;
  timezone: string;
  available: boolean;
  reason?: string | null;
  slots: AvailabilitySlot[];
}

export interface AvailableDateConfig {
  date: string;
  times: string[];
}

export const BOOKING_TIMEZONE = 'Asia/Riyadh';

export interface AdminStatsResponse {
  users: number;
  bookings: number;
  messages: number;
}

export interface AdminChartPoint {
  name: string;
  newUsers: number;
  totalUsers: number;
}

export interface UserStatisticsResponse {
  totalUsers: number;
  regularUsers: number;
  admins: number;
}

export interface BookingStatisticsResponse {
  total: number;
  pending: number;
  confirmed: number;
  cancelled: number;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  page: number;
  size: number;
  totalPages: number;
}

export interface UserView {
  userId: string | null;
  viewerName?: string | null;
  viewerEmail?: string | null;
  count: number;
}

export interface PageView {
  id?: string;
  pagePath: string;
  views: number;
  userViews?: UserView[];
}
